import { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, Headphones, Send, Video, VideoOff, Trash2, RefreshCw, CheckCircle2, Building2, Activity, Wrench, Plus, FileText, KeyRound, ExternalLink, Clock, MessageSquare, X, History } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const QINP = "w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none";
const STATUS = {
  open: ["Open", "bg-[#c9a961]/20 text-[#8a6d2f]"],
  active: ["Active", "bg-[#2f7d5b]/15 text-[#2f7d5b]"],
  resolved: ["Resolved", "bg-[#1e3d3a]/8 text-[#1e3d3a]/55"],
};

// SLA: how long we've kept the client waiting. Amber >1h, red >4h.
const waited = (since, now) => {
  if (!since) return null;
  const ms = now - new Date(since).getTime();
  if (ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  const h = Math.floor(mins / 60), m = mins % 60;
  const text = mins < 1 ? "just now" : h > 0 ? `${h}h ${m}m` : `${m}m`;
  const tone = mins >= 240 ? "red" : mins >= 60 ? "amber" : "ok";
  return { text, tone };
};
const SLA_TONE = {
  ok: "bg-[#2f7d5b]/12 text-[#2f7d5b]",
  amber: "bg-[#c9a961]/25 text-[#8a6d2f]",
  red: "bg-[#b23a48]/15 text-[#b23a48]",
};
const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return `${d}d ago`;
};

export default function SupportBoard() {
  const [sessions, setSessions] = useState([]);
  const [sel, setSel] = useState(null);
  const [account, setAccount] = useState(null);
  const [text, setText] = useState("");
  const [showVideo, setShowVideo] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [qform, setQform] = useState({ entity: "revenue", label: "", amount: "", date: "" });
  const [qbusy, setQbusy] = useState(false);
  const [canned, setCanned] = useState([]);
  const [manageCanned, setManageCanned] = useState(false);
  const [newCanned, setNewCanned] = useState({ title: "", text: "" });
  const [now, setNow] = useState(Date.now());
  const endRef = useRef(null);

  useEffect(() => { const iv = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(iv); }, []);

  const loadCanned = useCallback(async () => {
    try { const { data } = await api.get("/admin/support/canned-replies"); setCanned(data.replies || []); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { loadCanned(); }, [loadCanned]);

  const loadList = useCallback(async () => {
    try { const { data } = await api.get("/admin/support"); setSessions(data.sessions || []); }
    catch { toast.error("Could not load support sessions."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadList(); const iv = setInterval(loadList, 5000); return () => clearInterval(iv); }, [loadList]);

  const openSession = useCallback(async (sid) => {
    try {
      const [{ data: s }, { data: acc }] = await Promise.all([
        api.get(`/admin/support/${sid}`),
        api.get(`/admin/support/${sid}/account`).catch(() => ({ data: null })),
      ]);
      setSel(s); setAccount(acc);
      try { const { data: p } = await api.get(`/progress/${encodeURIComponent(s.client_id)}`); setProgress(p); }
      catch { setProgress(null); }
    } catch { toast.error("Could not open session."); }
  }, []);

  // Poll the open session AND its live account snapshot every 3s.
  useEffect(() => {
    if (!sel?.id) return;
    const iv = setInterval(async () => {
      try {
        const [{ data: s }, { data: acc }] = await Promise.all([
          api.get(`/admin/support/${sel.id}`),
          api.get(`/admin/support/${sel.id}/account`).catch(() => ({ data: account })),
        ]);
        setSel(s); if (acc) setAccount(acc);
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [sel?.id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [sel?.messages?.length]);

  const send = async () => {
    if (!text.trim() || !sel) return;
    const t = text; setText("");
    try { await api.post(`/admin/support/${sel.id}/messages`, { text: t, sender_name: "Elevare" }); const { data } = await api.get(`/admin/support/${sel.id}`); setSel(data); }
    catch { toast.error("Could not send."); setText(t); }
  };

  const createCanned = async () => {
    if (!newCanned.title.trim() || !newCanned.text.trim()) { toast.error("Add a title and message."); return; }
    try { await api.post("/admin/support/canned-replies", newCanned); setNewCanned({ title: "", text: "" }); await loadCanned(); toast.success("Saved reply."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
  };
  const deleteCanned = async (id) => {
    try { await api.delete(`/admin/support/canned-replies/${id}`); await loadCanned(); }
    catch { toast.error("Could not delete."); }
  };

  const setStatus = async (status) => {
    try { await api.post(`/admin/support/${sel.id}/status`, { status }); setSel((s) => ({ ...s, status })); await loadList(); }
    catch { toast.error("Could not update status."); }
  };

  const remove = async (sid) => {
    if (!window.confirm("Delete this support session?")) return;
    try { await api.delete(`/admin/support/${sid}`); if (sel?.id === sid) setSel(null); await loadList(); }
    catch { toast.error("Could not delete."); }
  };

  const addOpsEntry = async () => {
    if (!(Number(qform.amount) > 0)) { toast.error("Enter an amount."); return; }
    const cid = sel.client_id;
    const label = qform.label.trim() || "Support entry";
    const amount = Number(qform.amount);
    const date = qform.date || new Date().toISOString().slice(0, 10);
    const payload = qform.entity === "revenue" ? { source: label, amount, date }
      : qform.entity === "overhead" ? { name: label, amount, frequency: "Monthly", category: "Other" }
      : { name: label, amount, due_date: date, tax_type: "Other" };
    setQbusy(true);
    try {
      await api.post(`/admin/operations/${encodeURIComponent(cid)}/${qform.entity}`, payload);
      setQform({ ...qform, label: "", amount: "", date: "" });
      await openSession(sel.id);
      toast.success(`Added to ${qform.entity} on their account.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add the entry."); }
    finally { setQbusy(false); }
  };

  const toggleTask = async (idx) => {
    if (!progress) return;
    const tasks = (progress.tasks || []).map((t, i) => i === idx ? { ...t, done: !t.done } : t);
    setProgress({ ...progress, tasks });
    try { await api.put(`/admin/progress/${encodeURIComponent(sel.client_id)}`, { tasks: tasks.map((t) => ({ label: t.label, done: !!t.done })) }); }
    catch { toast.error("Could not update the task."); await openSession(sel.id); }
  };

  const sendReset = async (email) => {
    if (!window.confirm(`Send a password-reset link to ${email}?`)) return;
    try { await api.post(`/admin/support/${sel.id}/send-reset`, { email }); toast.success(`Reset link sent to ${email}.`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send reset."); }
  };
  const revokeUser = async (mid) => {
    if (!window.confirm("Revoke this user's access to the account?")) return;
    try { await api.post(`/admin/support/${sel.id}/revoke-user`, { member_id: mid }); toast.success("Access revoked."); await openSession(sel.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke."); }
  };
  const openDoc = async (did) => {
    try {
      const res = await api.get(`/admin/support/${sel.id}/documents/${did}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open the document."); }
  };

  if (loading) return <div className="flex items-center gap-2 text-[#1e3d3a]/60 p-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading support…</div>;

  const op = account?.operations || {};
  const roll = op.rollup || op.summary || {};

  return (
    <div data-testid="support-board">
      <div className="flex items-center gap-2 mb-4 text-[#1e3d3a]"><Headphones className="h-5 w-5 text-[#c9a961]" /><h2 className="font-head text-xl">Live Support</h2></div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Session list */}
        <div className="lg:col-span-1 space-y-2" data-testid="support-list">
          {sessions.length === 0 && <div className="rounded-xl border border-dashed border-[#1e3d3a]/15 p-6 text-center text-sm text-[#1e3d3a]/50">No help requests yet.</div>}
          {sessions.map((s) => (
            <button key={s.id} data-testid={`support-row-${s.id}`} onClick={() => openSession(s.id)}
              className={`w-full rounded-xl border px-3 py-2.5 text-left transition-colors ${sel?.id === s.id ? "border-[#c9a961] bg-[#c9a961]/8" : "border-[#1e3d3a]/12 hover:border-[#c9a961]/50"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-[#1e3d3a] truncate flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-[#c9a961]" />{s.client_name}</span>
                <div className="flex items-center gap-1.5 shrink-0">
                  {s.admin_unread > 0 && <span className="rounded-full bg-[#b23a48] px-1.5 py-0.5 text-[9px] font-bold text-white">{s.admin_unread}</span>}
                  <span className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wide ${(STATUS[s.status] || STATUS.open)[1]}`}>{(STATUS[s.status] || STATUS.open)[0]}</span>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <div className="text-[11px] text-[#1e3d3a]/50 truncate">{s.subject}</div>
                {(() => { const w = waited(s.waiting_since, now); return w ? (
                  <span data-testid={`support-sla-${s.id}`} className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold shrink-0 ${SLA_TONE[w.tone]}`}><Clock className="h-2.5 w-2.5" />{w.text}</span>
                ) : null; })()}
              </div>
            </button>
          ))}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {!sel ? (
            <div className="rounded-2xl border border-dashed border-[#1e3d3a]/15 p-10 text-center text-[#1e3d3a]/50">Pick a request to chat, see their account live, and jump on video.</div>
          ) : (
            <div className="space-y-4" data-testid="support-detail">
              <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#1e3d3a]">{sel.client_name} · {sel.subject}</div>
                    <div className="text-[11px] text-[#1e3d3a]/45 flex items-center gap-2">
                      <span>{sel.client_id}</span>
                      {(() => { const w = waited(sel.waiting_since, now); return w ? (
                        <span data-testid="support-sla-detail" className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${SLA_TONE[w.tone]}`}><Clock className="h-3 w-3" />{w.text === "just now" ? "Waiting for reply" : `Waiting ${w.text}`}</span>
                      ) : <span className="inline-flex items-center gap-1 text-[#2f7d5b]"><CheckCircle2 className="h-3 w-3" />Up to date</span>; })()}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {["open", "active", "resolved"].map((st) => (
                      <button key={st} data-testid={`support-status-${st}`} onClick={() => setStatus(st)} className={`rounded-full px-2.5 py-1 text-[10px] uppercase tracking-wide border ${sel.status === st ? "border-[#c9a961] bg-[#c9a961]/15 text-[#1e3d3a]" : "border-[#1e3d3a]/12 text-[#1e3d3a]/50"}`}>{st}</button>
                    ))}
                    <button data-testid="support-delete" onClick={() => remove(sel.id)} className="p-1.5 text-[#1e3d3a]/30 hover:text-[#b23a48]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>

                <button data-testid="support-video-toggle" onClick={() => setShowVideo((v) => !v)} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972]">
                  {showVideo ? <VideoOff className="h-4 w-4" /> : <Video className="h-4 w-4" />} {showVideo ? "Leave video" : "Join video call"}
                </button>
                {showVideo && (
                  <div className="mt-3 overflow-hidden rounded-xl border border-[#1e3d3a]/15" data-testid="support-video-frame">
                    <iframe title="Elevare support video" src={`https://meet.jit.si/${sel.video_room}#config.prejoinPageEnabled=false`}
                      allow="camera; microphone; fullscreen; display-capture; autoplay" className="w-full" style={{ height: 420, border: 0 }} />
                  </div>
                )}
              </div>

              {/* Chat */}
              <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-4">
                <div className="max-h-64 overflow-y-auto rounded-xl bg-[#f7f5ef] p-3 space-y-2" data-testid="support-chat">
                  {(sel.messages || []).map((m) => (
                    <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === "admin" ? "bg-[#1e3d3a] text-white" : "bg-white border border-[#1e3d3a]/10 text-[#1e3d3a]"}`}>
                        <div className="text-[10px] uppercase tracking-wide opacity-60 mb-0.5">{m.sender === "admin" ? m.sender_name : sel.client_name}</div>
                        {m.text}
                      </div>
                    </div>
                  ))}
                  <div ref={endRef} />
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex flex-wrap gap-1.5" data-testid="support-canned-chips">
                    {canned.map((c) => (
                      <button key={c.id} data-testid={`support-canned-${c.id}`} title={c.text}
                        onClick={() => setText(c.text)}
                        className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/12 bg-[#f7f5ef] px-2.5 py-1 text-[11px] text-[#1e3d3a]/75 hover:border-[#c9a961] hover:text-[#1e3d3a]">
                        <MessageSquare className="h-3 w-3 text-[#c9a961]" />{c.title}
                      </button>
                    ))}
                    <button data-testid="support-canned-manage" onClick={() => setManageCanned((v) => !v)} className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] text-[#a3823f] hover:underline">{manageCanned ? "Done" : "Manage replies"}</button>
                  </div>
                </div>
                {manageCanned && (
                  <div className="mt-2 rounded-xl border border-[#1e3d3a]/10 bg-[#f7f5ef] p-3 space-y-2" data-testid="support-canned-manager">
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input data-testid="canned-title-input" className={`${QINP} sm:w-40`} placeholder="Title" value={newCanned.title} onChange={(e) => setNewCanned({ ...newCanned, title: e.target.value })} />
                      <input data-testid="canned-text-input" className={QINP} placeholder="Message text…" value={newCanned.text} onChange={(e) => setNewCanned({ ...newCanned, text: e.target.value })} />
                      <button data-testid="canned-add" onClick={createCanned} className="inline-flex items-center justify-center gap-1 rounded-full bg-[#1e3d3a] px-3 py-2 text-sm font-semibold text-white hover:bg-[#2a534d]"><Plus className="h-4 w-4" /></button>
                    </div>
                    {canned.map((c) => (
                      <div key={c.id} className="flex items-center justify-between gap-2 text-[12px] text-[#1e3d3a]/70">
                        <span className="truncate"><b>{c.title}</b> · {c.text}</span>
                        <button data-testid={`canned-delete-${c.id}`} onClick={() => deleteCanned(c.id)} className="p-1 text-[#1e3d3a]/30 hover:text-[#b23a48] shrink-0"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-2 flex gap-2">
                  <input data-testid="support-reply-input" className="flex-1 rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none" placeholder="Reply to the client…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} />
                  <button data-testid="support-reply-send" onClick={send} className="rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d]"><Send className="h-4 w-4" /></button>
                </div>
              </div>

              {/* Live account view */}
              <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-4" data-testid="support-account">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-[#1e3d3a]"><Activity className="h-4 w-4 text-[#c9a961]" /><span className="text-sm font-semibold">Live account view</span></div>
                  <button data-testid="support-account-refresh" onClick={() => openSession(sel.id)} className="inline-flex items-center gap-1 text-xs text-[#a3823f] hover:underline"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
                </div>
                {!account ? (
                  <p className="text-sm text-[#1e3d3a]/50">No account snapshot available.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <Snap label="Business" value={account.vault?.business_name || account.business_name || "—"} />
                    <Snap label="Subscription" value={account.subscription?.locked ? "Locked" : (account.subscription?.status || "—")} tone={account.subscription?.locked ? "red" : "ok"} />
                    <Snap label="Identity" value={account.vault?.identity_verified ? "Verified" : "Not verified"} />
                    <Snap label="Banks on file" value={account.vault?.bank_accounts ?? 0} />
                    {account.progress && <Snap label="BBB" value={`${account.progress.current_bbb || "—"} → ${account.progress.target_bbb || "—"}`} />}
                    {account.progress && <Snap label="Tasks done" value={`${account.progress.tasks_done}/${account.progress.tasks_total}`} />}
                    {roll.revenue != null && <Snap label="Revenue" value={money(roll.revenue)} />}
                    {roll.net_margin != null && <Snap label="Net margin" value={`${Math.round(roll.net_margin)}%`} />}
                  </div>
                )}
                {(account?.open_recommendations || []).length > 0 && (
                  <div className="mt-3 border-t border-[#1e3d3a]/8 pt-3">
                    <div className="text-[11px] uppercase tracking-wide text-[#1e3d3a]/45 mb-1">Open recommendations</div>
                    {account.open_recommendations.map((r) => <div key={r.id} className="text-sm text-[#1e3d3a]/80">• {r.problem}</div>)}
                  </div>
                )}
                <p className="mt-3 text-[11px] text-[#1e3d3a]/45">Fix this account on their behalf below, or from the <b>Progress</b>, <b>CRM</b> and <b>Expense AI</b> tabs — every admin action there is scoped to this client.</p>
              </div>

              {/* In-session quick fixes */}
              <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-4" data-testid="support-quickfixes">
                <div className="flex items-center gap-2 mb-3 text-[#1e3d3a]"><Wrench className="h-4 w-4 text-[#c9a961]" /><span className="text-sm font-semibold">Quick fixes on their account</span></div>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                  <select data-testid="quickfix-entity" className={QINP} value={qform.entity} onChange={(e) => setQform({ ...qform, entity: e.target.value })}>
                    <option value="revenue">Log revenue</option>
                    <option value="overhead">Log overhead</option>
                    <option value="taxes">Log tax item</option>
                  </select>
                  <input data-testid="quickfix-label" className={QINP} placeholder="Label / source" value={qform.label} onChange={(e) => setQform({ ...qform, label: e.target.value })} />
                  <input data-testid="quickfix-amount" className={QINP} type="number" placeholder="Amount $" value={qform.amount} onChange={(e) => setQform({ ...qform, amount: e.target.value })} />
                  <input data-testid="quickfix-date" className={QINP} type="date" value={qform.date} onChange={(e) => setQform({ ...qform, date: e.target.value })} />
                  <button data-testid="quickfix-add" onClick={addOpsEntry} disabled={qbusy} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">{qbusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add</button>
                </div>
                {progress?.tasks?.length > 0 && (
                  <div className="mt-4 border-t border-[#1e3d3a]/8 pt-3">
                    <div className="text-[11px] uppercase tracking-wide text-[#1e3d3a]/45 mb-1.5">Progress tasks — tap to mark done</div>
                    <div className="space-y-1.5" data-testid="quickfix-tasks">
                      {progress.tasks.map((t, i) => (
                        <label key={i} data-testid={`quickfix-task-${i}`} className="flex items-center gap-2 text-sm text-[#1e3d3a] cursor-pointer">
                          <input type="checkbox" checked={!!t.done} onChange={() => toggleTask(i)} />
                          <span className={t.done ? "line-through text-[#1e3d3a]/40" : ""}>{t.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Documents on file */}
              <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-4" data-testid="support-documents">
                <div className="flex items-center gap-2 mb-3 text-[#1e3d3a]"><FileText className="h-4 w-4 text-[#c9a961]" /><span className="text-sm font-semibold">Documents ({(account?.documents || []).length})</span></div>
                {(account?.documents || []).length === 0 ? (
                  <p className="text-sm text-[#1e3d3a]/50">No documents uploaded yet.</p>
                ) : (
                  <ul className="divide-y divide-[#1e3d3a]/8">
                    {account.documents.map((d) => (
                      <li key={d.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                        <span className="text-[#1e3d3a] truncate">{d.filename || d.name || d.title || d.id}{d.category ? ` · ${d.category}` : ""}</span>
                        <button data-testid={`support-doc-${d.id}`} onClick={() => openDoc(d.id)} className="inline-flex items-center gap-1 text-[11px] text-[#a3823f] hover:underline shrink-0">Open <ExternalLink className="h-3 w-3" /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Users & credentials */}
              <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-4" data-testid="support-users">
                <div className="flex items-center gap-2 mb-1 text-[#1e3d3a]"><KeyRound className="h-4 w-4 text-[#c9a961]" /><span className="text-sm font-semibold">Users & access</span></div>
                <p className="text-[11px] text-[#1e3d3a]/45 mb-3">Passwords are encrypted and can't be shown. Send a reset link or revoke access instead.</p>
                <ul className="divide-y divide-[#1e3d3a]/8">
                  {(account?.users || []).filter((u) => u.active || showRevoked).map((u) => (
                    <li key={u.email + (u.member_id || "")} data-testid={`support-user-${u.member_id || 'owner'}`} className="flex items-center justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <div className="text-sm text-[#1e3d3a] truncate">{u.name} <span className="text-[#1e3d3a]/45">· {u.email}</span></div>
                        <div className="text-[11px] text-[#1e3d3a]/50">
                          {u.role} · {u.permission === "owner" ? "full (owner)" : u.permission}
                          {!u.active && <span className="text-[#b23a48]"> · revoked</span>}
                        </div>
                        {u.reset_sent_at && (
                          <div data-testid={`support-reset-status-${u.member_id || 'owner'}`} className="mt-0.5 text-[10px]">
                            {u.reset_used ? (
                              <span className="inline-flex items-center gap-1 text-[#2f7d5b]"><CheckCircle2 className="h-3 w-3" />Reset used {timeAgo(u.reset_used_at)} — back in</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[#8a6d2f]"><Clock className="h-3 w-3" />Reset sent {timeAgo(u.reset_sent_at)} · awaiting</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button data-testid={`support-reset-${u.member_id || 'owner'}`} onClick={() => sendReset(u.email)} className="rounded-full border border-[#1e3d3a]/15 px-2.5 py-1 text-[10px] uppercase tracking-wide text-[#1e3d3a]/70 hover:border-[#c9a961]">Send reset</button>
                        {u.member_id && u.active && <button data-testid={`support-revoke-${u.member_id}`} onClick={() => revokeUser(u.member_id)} className="rounded-full border border-[#b23a48]/30 px-2.5 py-1 text-[10px] uppercase tracking-wide text-[#b23a48] hover:bg-[#b23a48]/8">Revoke</button>}
                      </div>
                    </li>
                  ))}
                </ul>
                {(account?.users || []).filter((u) => !u.active).length > 0 && (
                  <button data-testid="support-toggle-revoked" onClick={() => setShowRevoked((v) => !v)} className="mt-2 text-[11px] text-[#a3823f] hover:underline">
                    {showRevoked ? "Hide revoked" : `Show ${(account.users || []).filter((u) => !u.active).length} revoked`}
                  </button>
                )}
                {(account?.access_codes || []).length > 0 && (
                  <div className="mt-3 border-t border-[#1e3d3a]/8 pt-2">
                    <div className="text-[11px] uppercase tracking-wide text-[#1e3d3a]/45 mb-1">Access codes issued</div>
                    {account.access_codes.map((c) => (
                      <div key={c.id} className="flex items-center gap-2 text-[13px] text-[#1e3d3a]/80">
                        <span className="font-mono">{c.code}</span>
                        <span className="rounded-full bg-[#1e3d3a]/6 px-1.5 py-0.5 text-[9px] uppercase tracking-wide">{c.permission}</span>
                        {!c.active && <span className="text-[9px] uppercase text-[#1e3d3a]/40">off</span>}
                        {c.label && <span className="text-[#1e3d3a]/45">· {c.label}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Member activity log */}
              <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-4" data-testid="support-activity">
                <div className="flex items-center gap-2 mb-3 text-[#1e3d3a]"><History className="h-4 w-4 text-[#c9a961]" /><span className="text-sm font-semibold">Activity log</span></div>
                {(account?.activity || []).length === 0 ? (
                  <p className="text-sm text-[#1e3d3a]/50">No sign-ins or actions recorded yet.</p>
                ) : (
                  <ul className="space-y-1.5 max-h-64 overflow-y-auto">
                    {account.activity.map((a) => (
                      <li key={a.id} className="flex items-center justify-between gap-3 text-[13px]">
                        <span className="text-[#1e3d3a] truncate">
                          <span className={`inline-block h-1.5 w-1.5 rounded-full mr-2 align-middle ${a.type === "login" ? "bg-[#3b6ea5]" : "bg-[#c9a961]"}`} />
                          <b>{a.actor_name || a.actor_email}</b> · {a.label}
                        </span>
                        <span className="text-[11px] text-[#1e3d3a]/45 shrink-0">{timeAgo(a.at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Snap = ({ label, value, tone }) => (
  <div className="rounded-xl bg-[#1e3d3a]/5 px-3 py-2.5">
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/45">{label}</div>
    <div className={`text-sm font-semibold mt-0.5 ${tone === "red" ? "text-[#b23a48]" : tone === "ok" ? "text-[#2f7d5b]" : "text-[#1e3d3a]"}`}>{value}</div>
  </div>
);
